import React, { useState } from "react";
import { Box, Button, useTheme, Switch, Typography, CircularProgress, useMediaQuery } from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useDispatch, useSelector } from "react-redux";
import { toggleWorkflowStatus } from '../../redux/Slice/Workflow/workflowSlice';
import { isSubscriptionBlockedError, checkSubscriptionActive } from '../../utils/subscriptionError';
import { getAuthToken } from '../../utils/getAuthToken';
import { toast } from 'react-hot-toast';

const DEFAULT_TABS = ["Actions", "Analytics", "Runs", "Revisions"];

const WorkflowNavbar = ({
  activeTab,
  onTabChange,
  tabs = DEFAULT_TABS,
  isWorkflowOn = false,
  onToggleWorkflow,
  onToggleError,
  workflowName = "",
  onGoBack
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const dispatch = useDispatch();
  const { selectedWorkflow, toggleLoading } = useSelector(state => state.workflow);
  const workflowId = selectedWorkflow?._id;
  const status = selectedWorkflow?.status;
  
  // Local state for optimistic updates
  const [optimisticStatus, setOptimisticStatus] = useState(status);
  
  // Update optimistic status when actual status changes
  React.useEffect(() => {
    setOptimisticStatus(status);
  }, [status]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: `1px solid ${isDark ? "#333" : "#e0e0e0"}`,
        bgcolor: isDark ? "#23272b" : "#f5f7fa",
        px: { xs: 1, sm: 1.5, md: 1.5, lg: 2, xl: 2 },
        height: { xs: 40, sm: 44, md: 44, lg: 48, xl: 48 },
        position: "relative",
      }}
    >
      {/* Go back arrow on the far left */}
      <Box sx={{ 
        position: 'absolute', 
        left: { xs: 4, sm: 6, md: 6, lg: 8, xl: 8 }, 
        display: 'flex', 
        alignItems: 'center', 
        height: '100%' 
      }}>
        <Button
          onClick={onGoBack}
          sx={{ 
            minWidth: 0, 
            p: { xs: 0.25, sm: 0.375, md: 0.375, lg: 0.5, xl: 0.5 }, 
            color: isDark ? '#fff' : '#222', 
            borderRadius: '50%' 
          }}
          aria-label="Go back"
        >
          <ArrowBackIosNewIcon sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' } }} />
        </Button>
      </Box>
      {/* Workflow Name on the left */}
      {workflowName && !isXs && (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 14, sm: 15, md: 16, lg: 17, xl: 18 },
            color: theme.palette.primary.main,
            mr: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 3 },
            minWidth: { xs: 80, sm: 100, md: 110, lg: 120, xl: 120 },
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
            ml: { xs: 3.5, sm: 4, md: 4.5, lg: 5, xl: 5 }
          }}
        >
          {workflowName}
        </Typography>
      )}
      {/* Tabs: centered sm+; xs aligns left after back arrow */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          justifyContent: { xs: "flex-start", sm: "center" },
          pl: { xs: 5, sm: 0 },
          pr: { xs: 9, sm: 0 },
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {tabs.map((tab) => (
          <Button
            key={tab}
            onClick={() => onTabChange(tab)}
            disableRipple
            sx={{
              minWidth: 0,
              px: { xs: 1, sm: 1.5, md: 2.5, lg: 3, xl: 3 },
              py: { xs: 0.75, sm: 1, md: 1.375, lg: 1.5, xl: 1.5 },
              color: isDark ? (activeTab === tab ? "#fff" : "#b0b0b0") : (activeTab === tab ? "#222" : "#555"),
              fontWeight: activeTab === tab ? 600 : 400,
              fontSize: { xs: 11, sm: 12, md: 14, lg: 15, xl: 16 },
              borderRadius: 0,
              borderBottom: activeTab === tab ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
              background: "none",
              boxShadow: "none",
              textTransform: "none",
              transition: "color 0.2s, border-bottom 0.2s",
              '&:hover': {
                color: theme.palette.primary.main,
                background: "none",
              },
            }}
          >
            {tab}
          </Button>
        ))}
      </Box>
      {/* Toggle Switch on the right */}
      <Box sx={{ 
        position: "absolute", 
        right: { xs: 8, sm: 12, md: 16, lg: 20, xl: 24 }, 
        display: "flex", 
        alignItems: "center",
        gap: { xs: 0.5, sm: 0.75, md: 0.75, lg: 1, xl: 1 }
      }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: isDark ? "#fff" : "#222", 
            mr: { xs: 0.5, sm: 0.75, md: 0.75, lg: 1, xl: 1 },
            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' }
          }}
        >
          {optimisticStatus === 'on' ? "On" : "Off"}
        </Typography>
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <Switch
            checked={optimisticStatus === 'on'}
            disabled={toggleLoading}
            onChange={async () => {
              if (!workflowId) return;
              const { blocked, message } = await checkSubscriptionActive(getAuthToken);
              if (blocked) {
                toast.error(message, { id: 'subscription-blocked', duration: 5000 });
                return;
              }
              const newStatus = optimisticStatus === 'on' ? 'off' : 'on';
              setOptimisticStatus(newStatus);
              dispatch(toggleWorkflowStatus({
                id: workflowId,
                status: newStatus,
              }))
                .unwrap()
                .catch((payload) => {
                  setOptimisticStatus(status);
                  const msg = payload?.message || (typeof payload === 'string' ? payload : null) || "Failed to toggle workflow";
                  if (!isSubscriptionBlockedError(msg)) {
                    onToggleError?.(msg);
                  }
                });
            }}
            color="primary"
            inputProps={{ 'aria-label': 'workflow toggle' }}
            size={isXs || isSm ? 'small' : 'medium'}
            sx={{
              '&.Mui-disabled': {
                opacity: 0.7,
              }
            }}
          />
          {toggleLoading && (
            <CircularProgress
              size={isXs || isSm ? 16 : 20}
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                color: theme.palette.primary.main,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WorkflowNavbar; 